import {
  AssetEntity,
  AssetFolderEntity,
  AssetRecordInformation,
  AssetWithOwnersEntity,
  InitialStateAssetEntity,
  InitialStateAssetFolderEntity,
  MappedAssets,
} from '@hellodarwin/core/lib/features/entities';
import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/app-store';

export interface MappedAssetsFolders {
  [key: string]: AssetFolderEntity;
}

export default class AssetsAdapter {
  sortAssetsByDate = (a: AssetEntity, b: AssetEntity) =>
    (b.uploaded_at ? new Date(b.uploaded_at).getTime() : 0) -
    (a.uploaded_at ? new Date(a.uploaded_at).getTime() : 0);

  setAllFolders = (
    mappedFolders: MappedAssetsFolders,
    folders: AssetFolderEntity[],
  ) => {
    for (let folder of folders) {
      this.addOneFolder(mappedFolders, folder);
    }

    return mappedFolders;
  };

  setAllFoldersFromRecords = (
    mappedFolders: MappedAssetsFolders,
    assets: AssetWithOwnersEntity[],
  ) => {
    mappedFolders = assets.reduce((o, asset) => {
      if (asset.records) {
        for (const record of asset.records) {
          const content: AssetFolderEntity = {
            ...o[record.record_id],
            ...record,
            mapped_assets: {
              ...o[record.record_id]?.mapped_assets,
              [asset.asset_id]: asset,
            },
          };

          o = {
            ...o,
            [record.record_id]: content,
          };
        }
      }
      return o;
    }, {} as MappedAssetsFolders);

    return mappedFolders;
  };

  addRecordAssets = (
    mappedFolders: MappedAssetsFolders,
    assets: AssetEntity[],
    record: AssetRecordInformation,
  ) => {
    if (!assets.length) return mappedFolders;

    const folder: AssetFolderEntity = {
      record_id: record.record_id,
      record_name: record.record_name ?? record.record_id,
      record_type: record.record_type,
      asset_details: assets,
      mapped_assets: {},
      updated_at: assets[0].uploaded_at ?? new Date(),
      created_at: assets[0].created_at ?? new Date(),
    };

    mappedFolders[folder.record_id] = {
      ...folder,
      mapped_assets: {},
    };

    for (let asset of assets) {
      mappedFolders[folder.record_id].mapped_assets[asset.asset_id] = asset;
    }

    return mappedFolders;
  };

  addOneFolder = (
    mappedFolders: MappedAssetsFolders,
    folder: AssetFolderEntity,
  ) => {
    mappedFolders[folder.record_id] = {
      ...folder,
      mapped_assets: {},
    };
    if (folder.asset_details) {
      for (let asset of folder.asset_details) {
        mappedFolders[folder.record_id].mapped_assets[asset.asset_id] = asset;
      }
    }

    return mappedFolders;
  };

  selectAllFolders = (mappedFolders: MappedAssetsFolders) => {
    const folders: AssetFolderEntity[] = [];

    for (let folder of Object.values(mappedFolders)) {
      folders.push({
        ...folder,
        asset_details: Object.values(folder.mapped_assets),
      });
    }

    return folders;
  };

  selectAllAssets = (mappedFolders: MappedAssetsFolders) => {
    const assets: AssetEntity[] = [];

    for (let folder of Object.values(mappedFolders)) {
      assets.push(...Object.values(folder.mapped_assets));
    }

    return assets.sort(this.sortAssetsByDate);
  };

  selectAllRecordAssets = (
    mappedFolders: MappedAssetsFolders,
    record_id: string,
  ): AssetEntity[] => {
    if (!mappedFolders[record_id]) return [];

    const assets: AssetEntity[] = !!mappedFolders[record_id]
      ? Object.values(mappedFolders[record_id].mapped_assets)
      : [];

    return assets.sort(this.sortAssetsByDate);
  };

  selectAllMappedAssets = (
    mappedFolders: MappedAssetsFolders,
  ): MappedAssets => {
    let allAssets = Object.values(mappedFolders).reduce(
      (o, folder) => ({ ...o, ...folder.mapped_assets }),
      {} as MappedAssets,
    );

    return allAssets;
  };

  selectFolderById = (
    mappedFolders: MappedAssetsFolders,
    recordId: string,
  ): AssetFolderEntity => {
    if (Object.keys(mappedFolders).length === 0)
      return InitialStateAssetFolderEntity;

    return {
      ...mappedFolders[recordId],
      asset_details: !!mappedFolders[recordId]
        ? Object.values(mappedFolders[recordId].mapped_assets)
        : [],
    };
  };

  selectAssetById = (
    mappedFolders: MappedAssetsFolders,
    recordId: string,
    assetId: string,
  ): AssetEntity => {
    if (Object.keys(mappedFolders).length === 0) return InitialStateAssetEntity;

    return {
      ...mappedFolders[recordId].mapped_assets[assetId],
    };
  };

  selectAssetByIdWithoutRecord = (
    mappedFolders: MappedAssetsFolders,
    assetId: string,
  ): AssetEntity => {
    if (Object.keys(mappedFolders).length === 0) return InitialStateAssetEntity;

    let allAssets = this.selectAllMappedAssets(mappedFolders);

    return {
      ...allAssets[assetId],
    };
  };

  updateAsset = (
    mappedFolders: MappedAssetsFolders,
    asset: AssetWithOwnersEntity,
  ) => {
    if (!!asset.records?.length) {
      for (const record of asset.records) {
        if (!mappedFolders[record.record_id]) {
          const now = new Date();
          mappedFolders[record.record_id] = {
            record_id: record.record_id,
            record_name: record.record_name,
            updated_at: asset.uploaded_at ?? now,
            created_at: asset.created_at ?? now,
            record_type: record.record_type,
          } as AssetFolderEntity;
        }

        mappedFolders[record.record_id] = {
          ...mappedFolders[record.record_id],
          mapped_assets: {
            ...mappedFolders[record.record_id]?.mapped_assets,
            [asset.asset_id]: asset,
          },
        };
      }
    }
    return mappedFolders;
  };

  updateMultipleAssets = (
    mappedFolders: MappedAssetsFolders,
    assets: AssetWithOwnersEntity[],
  ) => {
    for (const key in assets) {
      mappedFolders = this.updateAsset(mappedFolders, assets[key]);
    }
    return mappedFolders;
  };

  removeAsset = (mappedFolders: MappedAssetsFolders, assetId: string) => {
    const nextFolders: MappedAssetsFolders = {};

    for (const [folderKey, folder] of Object.entries(mappedFolders)) {
      if (!(assetId in folder.mapped_assets)) {
        nextFolders[folderKey] = folder;
        continue;
      }

      const updatedAssets: MappedAssets = {
        ...folder.mapped_assets,
      };
      delete updatedAssets[assetId];

      nextFolders[folderKey] = {
        ...folder,
        mapped_assets: updatedAssets,
      };
    }

    return nextFolders;
  };

  getInitialState = (): MappedAssetsFolders => {
    return {};
  };
  getSelectors = () => {
    const selectFolders = (state: RootState) => state.assets.folders;
    const selectFolderId = (_: RootState, folder_id: string) => folder_id;
    const selectRecordId = (_: RootState, record_id: string) => record_id;
    const selectAssetId = (
      _: RootState,
      _folder_id: string,
      asset_id: string,
    ) => asset_id;

    return {
      selectAllFolders: createSelector(
        selectFolders,
        (folders) => this.selectAllFolders(folders),
      ),
      selectAllAssets: createSelector(
        selectFolders,
        (folders) => this.selectAllAssets(folders),
      ),
      selectAllMappedAssets: createSelector(
        [selectFolders],
        (folders) => this.selectAllMappedAssets(folders),
      ),
      selectAllRecordAssets: createSelector(
        [
          selectFolders,
          selectRecordId,
        ],
        (folders, record_id) => this.selectAllRecordAssets(folders, record_id),
      ),
      selectFolderById: createSelector(
        [selectFolders, selectFolderId],
        (folders, folder_id) => this.selectFolderById(folders, folder_id),
      ),
      selectAssetById: createSelector(
        [selectFolders, selectFolderId, selectAssetId],
        (folders, folder_id, asset_id) =>
          this.selectAssetById(folders, folder_id, asset_id),
      ),
      selectAssetByIdWithoutRecord: createSelector(
        [selectFolders, selectFolderId],
        (folders, folder_id) =>
          this.selectAssetByIdWithoutRecord(folders, folder_id),
      ),
    };
  };
}

export const createAssetsAdapter = () => {
  return new AssetsAdapter();
};
